// Forms engine - templates (backend/DTO/forms/form_template_dto.py,
// backend/Services/user_task/docs/forms_engine_architecture.md).
// Boxes are in PDF points with the origin at the page's bottom-left corner.

export enum FormTemplateStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
  ARCHIVED = "archived",
}

export enum FormFieldType {
  TEXT = "text",
  DATE = "date",
  CHECKBOX = "checkbox",
  CHOICE = "choice",
  SIGNATURE = "signature",
  IMAGE = "image",
}

export enum FormFieldEditableBy {
  AGENT = "agent",
  CLIENT = "client",
  NONE = "none",
}

export enum FormSigner {
  CLIENT = "client",
  AGENT = "agent",
}

export enum FormTextFit {
  SHRINK = "shrink",
  WRAP = "wrap",
  CLIP = "clip",
}

export enum FormFieldValidation {
  ID_NUMBER = "id_number",
  PHONE = "phone",
  DATE = "date",
}

export interface FormFieldBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FormDateParts {
  day: FormFieldBox;
  month: FormFieldBox;
  year: FormFieldBox;
  /** each digit in a cell of its own - day / month split in 2, the year in 4 (2 with a two-digit year format) */
  digit_cells?: boolean;
}

export interface FormFieldRepeat {
  rows: number;
  row_pitch: number;
}

/** One value of a source with a fixed list (gender, marital status ...) - a choice box marks one */
export interface FormChoiceOption {
  value: string;
  label: string;
}

/** source → its values ("contact.marital_status": [{value: "married", label: "נשוי/אה"}, ...]) */
export type FormChoiceOptions = Record<string, FormChoiceOption[]>;

export interface FormField {
  key: string;
  label?: string | null;
  page: number;
  box: FormFieldBox;
  type: FormFieldType;
  source?: string | null;
  /** text: always this text - not from the data and not editable (e.g. a company name the form leaves blank) */
  fixed_value?: string | null;
  choice_value?: string | null;
  /** a choice without a source: boxes with the same group name are one group (with a source - the source) */
  choice_group?: string | null;
  date_parts?: FormDateParts | null;
  date_format?: string;
  font_size?: number | null; // none: the form's size (settings)
  font_color?: string | null; // "#rrggbb" - none: the form's color
  align?: "right" | "left" | "center" | null;
  fit?: FormTextFit;
  cells?: number | null; // comb boxes: the box split into this many cells, one character in each (an ID = 9)
  required?: boolean;
  editable_by?: FormFieldEditableBy;
  signer?: FormSigner | null;
  repeat?: FormFieldRepeat | null;
  validation?: FormFieldValidation | null;
}

/**
 * The whole form's defaults: a field uses font_size / font_color unless it sets its own; a box at the default
 * text height / checkbox size follows them when they change (a box sized by hand keeps its size).
 */
export interface FormTemplateSettings {
  font_size: number;
  font_color: string;
  checkbox_size: number;
  text_height: number;
  /** a text field's alignment unless it sets its own - auto: right for Hebrew, else left */
  align: FormTextAlign;
}

export enum FormTextAlign {
  CENTER = "center",
  RIGHT = "right",
  LEFT = "left",
  AUTO = "auto",
}

export const DEFAULT_FORM_SETTINGS: FormTemplateSettings = {
  font_size: 11,
  font_color: "#000000",
  checkbox_size: 10,
  text_height: 14,
  align: FormTextAlign.CENTER,
};

export interface FormPageSize {
  width: number;
  height: number;
}

export interface FormTemplateVersion {
  id: number;
  template_id: number;
  version: number;
  pdf_path: string;
  pdf_sha256: string;
  pages: FormPageSize[];
  fields: FormField[];
  settings?: Partial<FormTemplateSettings> | null;
  published_at?: string | null;
  created_by_user_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface FormTemplate {
  id: number;
  code: string;
  name: string;
  handler_code?: string | null;
  change_types: string[]; // SimulationChangeType values - the change the task asks for (its simulation row)
  product_types: number[]; // product / adjusted product type codes - empty: any
  status: FormTemplateStatus;
  published_version_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface FormTemplateDetails {
  template: FormTemplate;
  versions: FormTemplateVersion[];
}

export interface CreateFormTemplateRequest {
  code?: string; // internal - generated by the server when not given
  name: string;
  handler_code?: string | null;
  change_types: string[];
  product_types: number[];
}

/** The name and which tasks use the template (the code never changes) */
export interface UpdateFormTemplateRequest {
  name: string;
  handler_code?: string | null;
  change_types: string[];
  product_types: number[];
}

/** A data path a field can be filled from (the form data builder's dictionary) and its Hebrew label */
export interface FormSource {
  path: string; // "contact.first_name", "beneficiaries[{i}].last_name"
  label: string; // "שם פרטי", "שם משפחה של מוטב"
}
